import { RequestBuilder } from '@athena-lib/request-builder';
import type { ProductItem } from '@stickerapp-org/nomisma';
import type { ProductAndPricesInterface } from '../model/ProductAndPricesInterface';
import type { FileAppData, PriceAndQuantityOption, StickerWizardProductData } from '../types';

type UploadQuotesResponse = {
  orderId: number;
  ticketId: number;
};

type Extras = {
  material?: string;
  showVat?: boolean;
  quantity?: number;
  width?: string;
  height?: string;
};
export class StickerWizardService {
  protected qb: RequestBuilder;

  constructor(public baseURL: string = '') {
    this.qb = new RequestBuilder(baseURL);
  }

  public async fetchProductItem(
    productItem: ProductItem,
    lang: string,
    showVat: boolean,
  ): Promise<ProductAndPricesInterface> {
    return (await this.qb
      .createRequest()
      .setURL(
        `/product/create_item/${productItem.getProductFamilyName()}/${productItem.getProductName()}`,
      )
      .setURLParams({
        attributes: JSON.stringify(productItem.getAttributes()),
        prices: 'true',
        lang: lang,
        show_vat: showVat.toString(),
      })
      .execute()) as ProductAndPricesInterface;
  }

  public async getSheetPreview(productItem: ProductItem, file: FileAppData): Promise<string> {
    return (await this.qb
      .createRequest()
      .setURL(`/quotes/sheet_preview`)
      .setURLParams({
        product_item: JSON.stringify(productItem),
        file: JSON.stringify(file),
      })
      .execute()) as string;
  }

  public async uploadQuotesData(data: unknown): Promise<UploadQuotesResponse> {
    return (await this.qb
      .createRequest()
      .setURL(`/quotes/create_quotes/`)
      .setMethod('POST')
      .setBody(JSON.stringify(data))
      .execute()) as UploadQuotesResponse;
  }

  public async getFormattedPrice(price: string, lang: string): Promise<PriceAndQuantityOption> {
    return (await this.qb
      .createRequest()
      .setURL(`/product/get_formatted_price/`)
      .setURLParams({
        price: price,
        lang: lang,
      })
      .execute()) as PriceAndQuantityOption;
  }

  public async fetchWizardData(
    productName: string,
    lang: string,
    extras: Extras,
  ): Promise<StickerWizardProductData> {
    const params: Record<string, unknown> = {
      lang: lang,
    };

    if (extras.material) {
      params['material'] = extras.material;
    }

    if (extras.showVat) {
      params['showVat'] = extras.showVat;
    }

    if (extras.quantity) {
      params['quantity'] = extras.quantity;
    }

    if (extras.width) {
      params['width_mm'] = extras.width;
    }

    if (extras.height) {
      params['height_mm'] = extras.height;
    }

    const json = await this.getWizardData(productName, params);
    if (!Object.prototype.hasOwnProperty.call(json.productItem.attributes, 'feature')) {
      json.productItem.attributes.feature = [];
    }

    return json;
  }

  private async getWizardData(
    productName: string,
    params: Record<string, unknown>,
  ): Promise<StickerWizardProductData> {
    return (await this.qb
      .createRequest()
      .setURL(`/product/wizard_data/${productName}`)
      .setURLParams(params)
      .execute()) as StickerWizardProductData;
  }
}
